import React, { useEffect, useState } from 'react';
import { fastapiService } from '../../services/fastapiService';
import { X, CheckCircle, AlertCircle } from 'lucide-react';

interface WorkspaceEmailProviderModalProps {
  workspaceId: string;
  workspaceName: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const WorkspaceEmailProviderModal: React.FC<WorkspaceEmailProviderModalProps> = ({
  workspaceId,
  workspaceName,
  isOpen,
  onClose,
  onSaved
}) => {
  const [provider, setProvider] = useState<'resend' | 'mailgun'>('resend');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [configStatus, setConfigStatus] = useState<{
    mailgun_key_assigned?: boolean;
    verified_domain_count?: number;
    will_use_mailgun_for_sending?: boolean;
    mailgun_ready?: boolean;
    hints?: string[];
  } | null>(null);

  useEffect(() => {
    if (isOpen && workspaceId) {
      loadProvider();
      loadConfigStatus();
    }
  }, [isOpen, workspaceId]);

  const loadProvider = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fastapiService.getWorkspaceEmailProvider(workspaceId);
      const data = res?.data || res;
      if (data?.provider) {
        setProvider(data.provider as 'resend' | 'mailgun');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load email provider');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigStatus = async () => {
    try {
      const res = await fastapiService.getWorkspaceEmailConfigStatus(workspaceId);
      const data = res?.data || res;
      if (data) {
        setConfigStatus(data);
      }
    } catch {
      setConfigStatus(null);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await fastapiService.updateWorkspaceEmailProvider(workspaceId, provider);
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-admin-200">
          <h2 className="text-lg font-semibold text-admin-900">Email Sender Configuration</h2>
          <button onClick={onClose} className="text-admin-500 hover:text-admin-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-admin-600 mb-4">
            Configure which email provider to use for <strong>{workspaceName}</strong>. Domain verification and email sending will use this provider.
          </p>
          {configStatus && (
            <div className={`mb-4 p-3 rounded-lg border text-sm ${
              configStatus.mailgun_ready
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}>
              {configStatus.mailgun_ready ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  <span>Mailgun is fully configured. This workspace will send via Mailgun.</span>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium">Mailgun status</span>
                  </div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    <li>Mailgun key: {configStatus.mailgun_key_assigned ? 'Assigned' : 'Not assigned'}</li>
                    <li>Verified domains: {configStatus.verified_domain_count ?? 0}</li>
                    <li>Will use Mailgun: {configStatus.will_use_mailgun_for_sending ? 'Yes' : 'No'}</li>
                  </ul>
                  {configStatus.hints && configStatus.hints.length > 0 && (
                    <p className="mt-2 text-xs font-medium">Next steps: {configStatus.hints.join(' ')}</p>
                  )}
                </div>
              )}
            </div>
          )}
          {loading ? (
            <p className="text-admin-500">Loading...</p>
          ) : (
            <>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-admin-50"
                  style={{ borderColor: provider === 'resend' ? 'var(--primary-500)' : undefined }}>
                  <input
                    type="radio"
                    name="provider"
                    value="resend"
                    checked={provider === 'resend'}
                    onChange={() => setProvider('resend')}
                    className="text-primary-600"
                  />
                  <div>
                    <span className="font-medium text-admin-900">Resend</span>
                    <p className="text-xs text-admin-500">Use Resend.com for domain verification and sending</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-admin-50"
                  style={{ borderColor: provider === 'mailgun' ? 'var(--primary-500)' : undefined }}>
                  <input
                    type="radio"
                    name="provider"
                    value="mailgun"
                    checked={provider === 'mailgun'}
                    onChange={() => setProvider('mailgun')}
                    className="text-primary-600"
                  />
                  <div>
                    <span className="font-medium text-admin-900">Mailgun</span>
                    <p className="text-xs text-admin-500">Use Mailgun for domain verification and sending</p>
                  </div>
                </label>
              </div>
              {error && (
                <p className="mt-3 text-sm text-red-600">{error}</p>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-admin-200">
          <button onClick={onClose} className="admin-button-secondary px-4 py-2" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="admin-button-primary px-4 py-2" disabled={loading || saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
